import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Video, Phone, Check, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { logConsultationCallFailure } from "@/lib/consultationFailureLog";
import OwnerRatingPill from "@/components/OwnerRatingPill";

interface IncomingRequest {
  id: string;
  pet_owner_id: string;
  service_type: "virtual_video" | "virtual_phone";
  notes: string | null;
  contact_phone: string | null;
  pet_id: string | null;
  expires_at: string;
  created_at: string;
  status: string;
}

const RING_INTERVAL_MS = 2000;

/**
 * Mounted globally. When the logged-in user is a vet, listens for new
 * consultation_requests rows targeted at them, shows a blocking modal,
 * fires a browser notification + repeating beep, and lets them
 * accept / decline. Auto-dismisses when status changes or expires.
 */
export function IncomingConsultationListener() {
  const { user, session } = useAuth();
  const { role } = useUserRole();
  const navigate = useNavigate();
  const [active, setActive] = useState<IncomingRequest | null>(null);
  const [petName, setPetName] = useState<string>("");
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [responding, setResponding] = useState<"accept" | "decline" | null>(null);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const ringIntervalRef = useRef<number | null>(null);
  const browserNotifRef = useRef<Notification | null>(null);

  // Request browser notification permission once when a vet logs in
  useEffect(() => {
    if (role !== "vet" || typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      // Don't auto-prompt (intrusive); wait for first incoming. We'll prompt then.
    }
  }, [role]);

  // Subscribe to inserts targeting this vet
  useEffect(() => {
    if (!user || role !== "vet") return;
    const channel = supabase
      .channel(`incoming-consult:${user.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "consultation_requests",
        filter: `vet_user_id=eq.${user.id}`,
      }, (payload) => {
        const row = payload.new as IncomingRequest;
        if (row.status === "pending") setActive(row);
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "consultation_requests",
        filter: `vet_user_id=eq.${user.id}`,
      }, (payload) => {
        const row = payload.new as IncomingRequest;
        // If our active request was changed (cancelled/expired/etc) - close
        setActive((prev) => (prev && prev.id === row.id && row.status !== "pending" ? null : prev));
      })
      .subscribe();

    // Initial check for any pending request that already exists
    void (async () => {
      const { data } = await supabase
        .from("consultation_requests")
        .select("*")
        .eq("vet_user_id", user.id)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) setActive(data as IncomingRequest);
    })();

    return () => { void supabase.removeChannel(channel); };
  }, [user, role]);

  // Watch for on-demand appointments at this vet's practice that just became
  // paid, and auto-navigate the vet into the call room. Without this, the vet
  // sees nothing happen after the pet owner pays.
  useEffect(() => {
    if (!user || role !== "vet") return;
    let practiceIds: string[] = [];
    const navigatedRef = new Set<string>();

    const navigateIfReady = (apptId: string, paymentStatus: string, isOnDemand: boolean, callEndedAt: string | null) => {
      if (navigatedRef.has(apptId)) return;
      if (!isOnDemand) return;
      if (paymentStatus !== "paid") return;
      if (callEndedAt) return;
      navigatedRef.add(apptId);
      setWaitingForPayment(false);
      toast.success("Pet owner paid - joining call");
      navigate(`/consultation/${apptId}`);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    void (async () => {
      const { data: practices } = await supabase
        .from("vet_practices")
        .select("id")
        .eq("user_id", user.id);
      if (cancelled) return;
      practiceIds = (practices || []).map((p) => p.id);
      if (practiceIds.length === 0) return;

      // Catch any already-paid on-demand appointments waiting for us
      const { data: existing } = await supabase
        .from("appointments")
        .select("id, payment_status, is_on_demand, call_ended_at, call_started_at, created_at")
        .in("practice_id", practiceIds)
        .eq("is_on_demand", true)
        .eq("payment_status", "paid")
        .is("call_ended_at", null)
        .is("call_started_at", null)
        .gte("created_at", new Date(Date.now() - 10 * 60 * 1000).toISOString())
        .order("created_at", { ascending: false })
        .limit(1);
      if (cancelled) return;
      const ready = existing?.[0];
      if (ready) {
        navigateIfReady(ready.id, ready.payment_status, ready.is_on_demand, ready.call_ended_at);
      }

      const practiceSet = new Set(practiceIds);
      // Build the channel and attach listeners BEFORE subscribe(). Use a
      // unique name per effect run so a previous (still-tearing-down) instance
      // can't be re-fetched and have a second .on() added after .subscribe(),
      // which is what triggered the "cannot add postgres_changes callbacks
      // after subscribe()" runtime error in StrictMode / fast HMR.
      const channelName = `vet-appt-paid:${user.id}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      channel = supabase
        .channel(channelName)
        .on("postgres_changes", {
          event: "UPDATE",
          schema: "public",
          table: "appointments",
        }, (payload) => {
          const row = payload.new as { id: string; practice_id: string; payment_status: string; is_on_demand: boolean; call_ended_at: string | null };
          if (!practiceSet.has(row.practice_id)) return;
          navigateIfReady(row.id, row.payment_status, row.is_on_demand, row.call_ended_at);
        })
        .on("postgres_changes", {
          event: "INSERT",
          schema: "public",
          table: "appointments",
        }, (payload) => {
          const row = payload.new as { id: string; practice_id: string; payment_status: string; is_on_demand: boolean; call_ended_at: string | null };
          if (!practiceSet.has(row.practice_id)) return;
          navigateIfReady(row.id, row.payment_status, row.is_on_demand, row.call_ended_at);
        });
      if (cancelled) {
        void supabase.removeChannel(channel);
        channel = null;
        return;
      }
      channel.subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [user, role, navigate]);

  // When a request becomes active: start ringing, request permission, fire notification
  useEffect(() => {
    if (!active) {
      stopRinging();
      browserNotifRef.current?.close();
      browserNotifRef.current = null;
      return;
    }

    // Ring
    startRinging();

    // Browser notification
    if (typeof Notification !== "undefined") {
      const fire = () => {
        try {
          const n = new Notification(
            `Incoming ${active.service_type === "virtual_video" ? "video" : "audio"} call request`,
            {
              body: "A pet owner is waiting. Open GetPetVet to accept or decline.",
              tag: `consult-${active.id}`,
              requireInteraction: true,
              icon: "/favicon.ico",
            },
          );
          n.onclick = () => { window.focus(); n.close(); };
          browserNotifRef.current = n;
        } catch { /* ignore */ }
      };
      if (Notification.permission === "granted") fire();
      else if (Notification.permission === "default") {
        Notification.requestPermission().then((p) => { if (p === "granted") fire(); });
      }
    }

    // Pet name lookup
    if (active.pet_id) {
      void (async () => {
        const { data } = await supabase.from("pets").select("name, species").eq("id", active.pet_id!).maybeSingle();
        if (data?.name) setPetName(`${data.name}${data.species ? ` (${data.species})` : ""}`);
      })();
    } else {
      setPetName("");
    }

    return () => { stopRinging(); };
  }, [active?.id]);

  // Countdown
  useEffect(() => {
    if (!active) return;
    const expiresAt = new Date(active.expires_at).getTime();
    const tick = () => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) setActive(null);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [active]);

  const startRinging = () => {
    stopRinging();
    const playBeep = () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 880;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        o.start();
        o.stop(ctx.currentTime + 0.45);
      } catch { /* ignore */ }
    };
    playBeep();
    ringIntervalRef.current = window.setInterval(playBeep, RING_INTERVAL_MS);
  };

  const stopRinging = () => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
  };

  const respond = async (action: "accept" | "decline") => {
    if (!active) return;
    setResponding(action);
    try {
      const invokeRespond = () => supabase.functions.invoke("respond-consultation-request", {
        body: { requestId: active.id, action },
      });

      let { data, error } = await invokeRespond();
      let didRefresh = false;
      let refreshFailed = false;

      if (error?.message?.toLowerCase().includes("unauthorized") || !session) {
        try {
          const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session) refreshFailed = true;
        } catch {
          refreshFailed = true;
        }
        didRefresh = true;
        if (!refreshFailed) {
          ({ data, error } = await invokeRespond());
        }
      }

      if (refreshFailed || (error && didRefresh && error?.message?.toLowerCase().includes("unauthorized"))) {
        void logConsultationCallFailure({
          functionName: "respond-consultation-request",
          errorMessage: "Session refresh failed - vet must sign in again",
          stage: "session_refresh_failed",
          action,
          requestId: active.id,
          vetUserId: user?.id,
          petOwnerId: active.pet_owner_id,
          metadata: { refreshFailed, originalError: error?.message },
        });
        toast.error("Your session has expired", {
          description: "Please sign in again to respond to this call.",
          action: { label: "Sign in", onClick: () => navigate("/login?redirect=/vet-dashboard") },
        });
        return;
      }

      if (error) {
        void logConsultationCallFailure({
          functionName: "respond-consultation-request",
          errorMessage: error.message || "Unknown invoke error",
          stage: didRefresh ? "client_invoke_after_refresh" : "client_invoke",
          action,
          requestId: active.id,
          vetUserId: user?.id,
          petOwnerId: active.pet_owner_id,
          metadata: { didRefresh },
        });
        toast.error(error.message || "Couldn't respond");
        return;
      }
      if (action === "accept") {
        // Close dialog FIRST so the waiting banner is unobstructed, then
        // flip the banner state, then navigate. Order matters: state updates
        // are batched but unmounting the Dialog before showing the banner
        // avoids any visual race where users see "nothing happen".
        setActive(null);
        setWaitingForPayment(true);
        toast.success("Accepted - waiting for the pet owner to pay", {
          description: "You'll be taken into the call automatically once they complete payment.",
          duration: 8000,
        });
        window.setTimeout(() => setWaitingForPayment(false), 5 * 60 * 1000);
        if (!window.location.pathname.startsWith("/vet-dashboard")) {
          navigate("/vet-dashboard?tab=appointments");
        }
        return;
      } else {
        toast("Declined - we'll route the request elsewhere", { description: data?.nextRequestId ? "Sent to next available vet" : "No other vets online right now" });
      }
      setActive(null);
    } finally {
      setResponding(null);
    }
  };

  const waitingBanner = waitingForPayment ? (
    <div className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg border-2 border-primary bg-card text-card-foreground shadow-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4">
      <div className="relative flex h-3 w-3 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-primary" />
      </div>
      <div className="text-sm flex-1">
        <div className="font-semibold text-foreground">Waiting for pet owner to pay…</div>
        <div className="text-xs text-muted-foreground mt-0.5">You'll be taken into the call automatically.</div>
      </div>
      <button
        onClick={() => setWaitingForPayment(false)}
        className="ml-2 text-muted-foreground hover:text-foreground flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  ) : null;

  if (!active) return waitingBanner;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const Icon = active.service_type === "virtual_phone" ? Phone : Video;

  return (
    <>
      {waitingBanner}
      <Dialog open onOpenChange={() => { /* blocking */ }}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-primary animate-pulse" />
            Incoming {active.service_type === "virtual_video" ? "video" : "audio"} call
          </DialogTitle>
          <DialogDescription>
            A pet owner is waiting to start a consultation. Respond within the time limit.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="flex items-center justify-center">
            <div className="text-4xl font-bold tabular-nums text-foreground">
              {minutes}:{seconds.toString().padStart(2, "0")}
            </div>
          </div>
          {petName && (
            <div className="text-center text-sm text-muted-foreground">Patient: <span className="font-medium text-foreground">{petName}</span></div>
          )}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span>Owner feedback:</span>
            <OwnerRatingPill ownerId={active.pet_owner_id} size="xs" />
          </div>
          {active.notes && (
            <div className="text-sm bg-muted/40 rounded-md p-3 border border-border">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Owner's note</span>
              <p className="mt-1 text-foreground">{active.notes}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            disabled={!!responding}
            onClick={() => respond("decline")}
          >
            <X className="w-4 h-4 mr-1.5" /> Decline
          </Button>
          <Button
            disabled={!!responding}
            onClick={() => respond("accept")}
          >
            <Check className="w-4 h-4 mr-1.5" /> Accept
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
